import {
  AlertTriangle,
  Check,
  ChevronDown,
  Clock,
  ExternalLink,
  Loader2,
  Lock,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { env } from '@/env';
import { toast } from '@/lib/notify';
import { cn } from '@/lib/utils';

// Deep-link to the Console super-admin catalogue, where Studio prices are actually
// edited. Only available when the Console URL is configured (VITE_CONSOLE_URL).
const CONSOLE_CATALOGUE_URL = env.VITE_CONSOLE_URL
  ? `${env.VITE_CONSOLE_URL.replace(/\/$/, '')}/admin/catalog`
  : null;

import { useApiPricing } from '../api/useApiPricing';
import { useSyncPricing } from '../api/useSyncPricing';
import type { TenantApiPriceRow } from '../schemas/pricingSchemas';

const ALL_TENANTS = '__all__';

// Groups rows Console-category → our tag (PAN / Aadhaar), preserving first-seen order
// (the backend already returns them ordered by category, tag, operationId).
type Group = { category: string; tags: { tag: string; rows: TenantApiPriceRow[] }[] };

function groupRows(rows: TenantApiPriceRow[]): Group[] {
  const groups: Group[] = [];
  for (const row of rows) {
    let group = groups.find((g) => g.category === row.category);
    if (!group) {
      group = { category: row.category, tags: [] };
      groups.push(group);
    }
    let tag = group.tags.find((t) => t.tag === row.tag);
    if (!tag) {
      tag = { tag: row.tag, rows: [] };
      group.tags.push(tag);
    }
    tag.rows.push(row);
  }
  return groups;
}

// "10 credits" / "1 credit" — proper unit label.
function credits(value: number) {
  return `${value} ${value === 1 ? 'credit' : 'credits'}`;
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isCustomRate(row: TenantApiPriceRow) {
  return row.price != null && row.defaultPrice != null && row.defaultPrice !== row.price;
}

// Searchable tenant picker. A plain <select> is unusable once a super admin has many
// clients, so this is a filter-as-you-type combobox: click to open, type to narrow by
// name, click a row (or "All tenants") to select. Closes on outside-click / Escape.
function TenantCombobox({
  value,
  onChange,
  tenants,
}: {
  value: string;
  onChange: (v: string) => void;
  tenants: { id: string; name: string }[];
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selectedLabel =
    value === ALL_TENANTS
      ? `All tenants (${tenants.length})`
      : (tenants.find((t) => t.id === value)?.name ?? 'Select tenant');

  const q = query.trim().toLowerCase();
  const matches = q ? tenants.filter((t) => t.name.toLowerCase().includes(q)) : tenants;

  const choose = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex min-w-[220px] items-center justify-between gap-2 rounded-lg border border-grayEF bg-white px-3 py-1.5 text-sm text-black-logo transition-colors hover:border-primary/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown
          className={cn('size-4 shrink-0 text-gray66 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-[260px] overflow-hidden rounded-lg border border-grayEF bg-white shadow-elevation-2">
          <div className="flex items-center gap-2 border-b border-grayEF px-2.5 py-2">
            <Search className="size-4 shrink-0 text-gray66" />
            <input
              // biome-ignore lint/a11y/noAutofocus: focus the search on open for fast typing
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tenant…"
              className="w-full bg-transparent text-sm text-black-logo placeholder:text-gray66 focus:outline-none"
            />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                onClick={() => choose(ALL_TENANTS)}
                className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm text-black-logo hover:bg-[#F9FAFB]"
              >
                All tenants ({tenants.length})
                {value === ALL_TENANTS && <Check className="size-4 shrink-0 text-primary" />}
              </button>
            </li>
            {matches.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => choose(t.id)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm text-black-logo hover:bg-[#F9FAFB]"
                >
                  <span className="truncate">{t.name}</span>
                  {value === t.id && <Check className="size-4 shrink-0 text-primary" />}
                </button>
              </li>
            ))}
            {matches.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray66">No tenants match “{query}”.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export function ApiPricingScreen() {
  const { data: rows, isLoading, isError } = useApiPricing();
  const sync = useSyncPricing();
  const [tenant, setTenant] = useState<string>(ALL_TENANTS);
  // Collapsed section cards, keyed by "category::tag". Empty = all expanded.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggleSection = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const tenants = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows ?? []) map.set(r.tenantId, r.tenantName);
    return [...map].map(([id, name]) => ({ id, name }));
  }, [rows]);

  const filtered = useMemo(
    () => (rows ?? []).filter((r) => tenant === ALL_TENANTS || r.tenantId === tenant),
    [rows, tenant],
  );

  const showTenantColumn = tenant === ALL_TENANTS;
  const groups = useMemo(() => groupRows(filtered), [filtered]);

  const stats = useMemo(() => {
    const apis = new Set(filtered.map((r) => r.operationId)).size;
    const custom = filtered.filter(isCustomRate).length;
    const usingDefault = filtered.filter((r) => !r.isPriceSet).length;
    return { apis, custom, usingDefault };
  }, [filtered]);

  const lastSynced = useMemo(() => {
    const times = (rows ?? [])
      .map((r) => r.lastSyncedAt)
      .filter((t): t is string => Boolean(t))
      .sort();
    return times.length ? times[times.length - 1] : null;
  }, [rows]);

  const onRefresh = () => {
    sync.mutate(undefined, {
      onSuccess: (r) =>
        toast.success(
          `Synced ${r.tenantsSynced} tenant(s) · ${r.pricesUpserted} price(s) updated` +
            (r.tenantsSkipped ? ` · ${r.tenantsSkipped} skipped` : '') +
            (r.missingPrices ? ` · ${r.missingPrices} not set` : ''),
        ),
      onError: () => toast.error('Refresh failed — Console may be unreachable. Try again.'),
    });
  };

  return (
    <AppShell title="API Pricing">
      <div className="mx-auto w-full max-w-5xl">
        {/* Header */}
        <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-black-logo">API Pricing</h1>
            {!isError && (
              <p className="mt-1 text-sm text-gray66">
                Studio prices per call, in credits. Managed in Console — this view is read-only.
              </p>
            )}
          </div>
          {!isError && (
            <div className="flex flex-wrap items-center gap-2">
              {/* Prices are edited in Console — this opens its super-admin catalogue
                  in a new tab. Hidden when VITE_CONSOLE_URL isn't configured. */}
              {CONSOLE_CATALOGUE_URL && (
                <a
                  href={CONSOLE_CATALOGUE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outlined btn-small gap-2 text-primary! border-primary! hover:bg-primary/10!"
                >
                  <ExternalLink className="size-4" />
                  Edit in Console
                </a>
              )}
              <button
                type="button"
                onClick={onRefresh}
                disabled={sync.isPending}
                className="btn btn-primary btn-small gap-2"
              >
                <RefreshCw className={cn('size-4', sync.isPending && 'animate-spin')} />
                {sync.isPending ? 'Refreshing…' : 'Refresh from Console'}
              </button>
            </div>
          )}
        </div>

        {/* Controls row: tenant filter + last-synced + summary */}
        {!isLoading && !isError && filtered.length > 0 && (
          <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray66">Tenant</span>
              <TenantCombobox value={tenant} onChange={setTenant} tenants={tenants} />
            </div>

            {/* Summary chips */}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-[#F4F7FB] px-2.5 py-1 font-medium text-gray66">
                {stats.apis} APIs
              </span>
              {stats.custom > 0 && (
                <span className="rounded-full bg-[#EEF2FF] px-2.5 py-1 font-medium text-primary">
                  {stats.custom} custom rate{stats.custom === 1 ? '' : 's'}
                </span>
              )}
              {stats.usingDefault > 0 && (
                <span className="rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-700">
                  {stats.usingDefault} using default
                </span>
              )}
            </div>

            {lastSynced && (
              <span className="ms-auto inline-flex items-center gap-1.5 text-xs text-gray66">
                <Clock className="size-3.5" />
                Last synced {formatDateTime(lastSynced)}
              </span>
            )}
          </div>
        )}

        {/* Missing-price alert */}
        {!isLoading && !isError && stats.usingDefault > 0 && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <span>
              {stats.usingDefault} {stats.usingDefault === 1 ? 'API has' : 'APIs have'} no Studio
              price set in Console (showing the default). Set the price in Console, then Refresh.
            </span>
          </div>
        )}

        {/* States */}
        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray66">
            <Loader2 className="size-4 animate-spin" />
            Loading pricing…
          </div>
        )}
        {isError && (
          <div className="flex flex-col items-center justify-center rounded-xl border border-grayEF bg-white px-4 py-16 text-center">
            <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-[#F4F7FB]">
              <Lock className="size-5 text-gray66" />
            </div>
            <p className="text-sm font-medium text-black-logo">Pricing isn’t available here</p>
            <p className="mt-1 max-w-sm text-sm text-gray66">
              This page is managed by your Studio administrator. If you need pricing details, please
              reach out to them.
            </p>
          </div>
        )}
        {!isLoading && !isError && filtered.length === 0 && (
          <div className="rounded-xl border border-grayEF bg-white px-4 py-12 text-center text-sm text-gray66">
            No APIs to show yet. Click <span className="font-medium">Refresh from Console</span> to
            pull the latest.
          </div>
        )}

        {/* Grouped grid */}
        {!isLoading &&
          !isError &&
          groups.map((group) => (
            <section key={group.category} className="mb-7">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray66">
                {group.category}
              </h2>
              {group.tags.map((tagGroup) => {
                const sectionKey = `${group.category}::${tagGroup.tag}`;
                const isCollapsed = collapsed.has(sectionKey);
                return (
                  <div
                    key={tagGroup.tag}
                    className="mb-4 overflow-hidden rounded-xl border border-grayEF bg-white shadow-elevation-1"
                  >
                    <button
                      type="button"
                      onClick={() => toggleSection(sectionKey)}
                      aria-expanded={!isCollapsed}
                      className={cn(
                        'flex w-full items-center justify-between bg-[#F9FAFB] px-4 py-2.5 text-left transition-colors hover:bg-[#F1F4F8]',
                        !isCollapsed && 'border-b border-grayEF',
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <ChevronDown
                          className={cn(
                            'size-4 text-gray66 transition-transform',
                            isCollapsed && '-rotate-90',
                          )}
                        />
                        <span className="text-sm font-semibold text-black-logo">
                          {tagGroup.tag}
                        </span>
                      </span>
                      <span className="text-xs text-gray66">
                        {tagGroup.rows.length} {tagGroup.rows.length === 1 ? 'entry' : 'entries'}
                      </span>
                    </button>
                    {!isCollapsed && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                              <th className="px-4 py-2 font-medium">API</th>
                              {showTenantColumn && (
                                <th className="px-4 py-2 font-medium">Tenant</th>
                              )}
                              <th className="px-4 py-2 font-medium">Price / call</th>
                              <th className="px-4 py-2 font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tagGroup.rows.map((row) => {
                              const custom = isCustomRate(row);
                              return (
                                <tr
                                  key={`${row.tenantId}:${row.operationId}`}
                                  className="border-t border-grayEF transition-colors hover:bg-[#F9FAFB]"
                                >
                                  <td className="px-4 py-2.5 text-black-logo">{row.apiName}</td>
                                  {showTenantColumn && (
                                    <td className="px-4 py-2.5 text-gray66">{row.tenantName}</td>
                                  )}
                                  <td className="px-4 py-2.5">
                                    {row.price != null ? (
                                      <span className="inline-flex items-baseline gap-1.5">
                                        {custom && (
                                          <span className="text-gray-400 line-through">
                                            {credits(row.defaultPrice as number)}
                                          </span>
                                        )}
                                        <span
                                          className={cn(
                                            custom
                                              ? 'font-semibold text-primary'
                                              : 'text-black-logo',
                                          )}
                                        >
                                          {credits(row.price)}
                                        </span>
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">—</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2.5">
                                    {custom ? (
                                      <span
                                        className="inline-flex rounded-full bg-[#EEF2FF] px-2 py-0.5 text-xs font-medium text-primary"
                                        title="This tenant has a custom rate that differs from the standard price."
                                      >
                                        Custom rate
                                      </span>
                                    ) : row.isPriceSet ? (
                                      <span className="inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                                        Set
                                      </span>
                                    ) : (
                                      <span
                                        className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
                                        title="No Studio price set in Console — showing the catalogue default."
                                      >
                                        Default
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          ))}
      </div>
    </AppShell>
  );
}
